'use client'

import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdArrowBack,
    MdEdit,
    MdDelete,
    MdTableChart,
    MdAdd,
    MdSearch,
    MdContentCopy,
    MdUpload,
} from 'react-icons/md'
import { useAuth } from '@/store/hooks'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import {
    getPriceTable,
    deletePriceTable,
    duplicatePriceTable,
    getPriceTableItems,
    deletePriceTableItem,
    PriceTable,
    PriceTableItem,
    PRICE_TABLE_TYPE_LABELS,
    PRICE_TABLE_STATUS_LABELS,
    PRICE_TABLE_STATUS_COLORS,
} from '@/lib/api/billing/price-table-api'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

export default function PriceTableDetailPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions(
        'billing',
        'price_tables'
    )

    const [priceTable, setPriceTable] = useState<PriceTable | null>(null)
    const [items, setItems] = useState<PriceTableItem[]>([])
    const [loading, setLoading] = useState(true)
    const [loadingItems, setLoadingItems] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [showDuplicateModal, setShowDuplicateModal] = useState(false)
    const [duplicateName, setDuplicateName] = useState('')
    const [searchTerm, setSearchTerm] = useState('')
    const [deleteItemId, setDeleteItemId] = useState<number | null>(null)

    useEffect(() => {
        if (id && user?.companyId) {
            loadPriceTable()
            loadItems()
        }
    }, [id, user?.companyId])

    const loadPriceTable = async () => {
        if (!id) return

        setLoading(true)
        try {
            const response = await getPriceTable(parseInt(id, 10))
            if (response.success && response.result) {
                setPriceTable(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar tabela')
                navigate('/billing/price-tables')
            }
        } catch (error) {
            toast.error('Erro ao carregar tabela')
            navigate('/billing/price-tables')
        } finally {
            setLoading(false)
        }
    }

    const loadItems = async () => {
        if (!id) return

        setLoadingItems(true)
        try {
            const response = await getPriceTableItems(parseInt(id, 10))
            if (response.success) {
                setItems(response.result || [])
            }
        } catch (error) {
            console.error('Erro ao carregar itens:', error)
        } finally {
            setLoadingItems(false)
        }
    }

    const handleDelete = async () => {
        if (!id) return

        try {
            const response = await deletePriceTable(parseInt(id, 10))
            if (response.success) {
                toast.success('Tabela excluída com sucesso')
                navigate('/billing/price-tables')
            } else {
                toast.error(response.error || 'Erro ao excluir tabela')
            }
        } catch (error) {
            toast.error('Erro ao excluir tabela')
        } finally {
            setShowDeleteModal(false)
        }
    }

    const handleDuplicate = async () => {
        if (!id || !duplicateName.trim()) return

        try {
            const response = await duplicatePriceTable(
                parseInt(id, 10),
                duplicateName.trim()
            )
            if (response.success && response.result) {
                toast.success('Tabela duplicada com sucesso')
                navigate(`/billing/price-tables/${response.result.id}`)
            } else {
                toast.error(response.error || 'Erro ao duplicar tabela')
            }
        } catch (error) {
            toast.error('Erro ao duplicar tabela')
        } finally {
            setShowDuplicateModal(false)
            setDuplicateName('')
        }
    }

    const handleDeleteItem = async (itemId: number) => {
        if (!id) return

        try {
            const response = await deletePriceTableItem(
                parseInt(id, 10),
                itemId
            )
            if (response.success) {
                toast.success('Item removido com sucesso')
                loadItems()
            } else {
                toast.error(response.error || 'Erro ao remover item')
            }
        } catch (error) {
            toast.error('Erro ao remover item')
        } finally {
            setDeleteItemId(null)
        }
    }

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(value)
    }

    const filteredItems = items.filter(item => {
        if (!searchTerm) return true
        const search = searchTerm.toLowerCase()
        return (
            item.procedureCode.toLowerCase().includes(search) ||
            item.procedureName.toLowerCase().includes(search) ||
            item.category?.toLowerCase().includes(search)
        )
    })

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando tabela de preços...
                </p>
            </div>
        )
    }

    if (!priceTable) {
        return null
    }

    return (
        <div className="flex flex-col gap-6 p-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/billing/price-tables')}
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-lg" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                        <MdTableChart className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            {priceTable.name}
                        </h1>
                        <div className="flex items-center gap-2 mt-1">
                            <span className="badge badge-ghost">
                                {PRICE_TABLE_TYPE_LABELS[priceTable.type]}
                            </span>
                            <span
                                className={`badge ${PRICE_TABLE_STATUS_COLORS[priceTable.status]}`}
                            >
                                {PRICE_TABLE_STATUS_LABELS[priceTable.status]}
                            </span>
                        </div>
                    </div>
                </div>
                {(canCreate || canUpdate || canDelete) && (
                    <div className="flex items-center gap-2">
                        {canCreate && (
                            <button
                                onClick={() => {
                                    setDuplicateName(
                                        `${priceTable.name} (Copia)`
                                    )
                                    setShowDuplicateModal(true)
                                }}
                                className="btn btn-ghost btn-sm gap-2"
                            >
                                <MdContentCopy className="text-lg" />
                                <span className="hidden sm:inline">
                                    Duplicar
                                </span>
                            </button>
                        )}
                        {canUpdate && (
                            <button
                                onClick={() =>
                                    navigate(`/billing/price-tables/${id}/edit`)
                                }
                                className="btn btn-secondary btn-sm gap-2"
                            >
                                <MdEdit className="text-lg" />
                                <span className="hidden sm:inline">Editar</span>
                            </button>
                        )}
                        {canDelete && (
                            <button
                                onClick={() => setShowDeleteModal(true)}
                                className="btn btn-error btn-sm gap-2"
                            >
                                <MdDelete className="text-lg" />
                                <span className="hidden sm:inline">
                                    Excluir
                                </span>
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Info Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body p-4">
                        <p className="text-xs text-base-content/60 uppercase">
                            Convênio
                        </p>
                        <p className="font-semibold">
                            {priceTable.healthPlan?.name || 'Particular'}
                        </p>
                    </div>
                </div>
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body p-4">
                        <p className="text-xs text-base-content/60 uppercase">
                            Versão
                        </p>
                        <p className="font-semibold">
                            {priceTable.version || '-'}
                        </p>
                    </div>
                </div>
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body p-4">
                        <p className="text-xs text-base-content/60 uppercase">
                            Vigência
                        </p>
                        <p className="font-semibold">
                            {priceTable.validFrom
                                ? format(
                                      new Date(priceTable.validFrom),
                                      'dd/MM/yyyy',
                                      { locale: ptBR }
                                  )
                                : '-'}{' '}
                            {priceTable.validUntil &&
                                `- ${format(new Date(priceTable.validUntil), 'dd/MM/yyyy', { locale: ptBR })}`}
                        </p>
                    </div>
                </div>
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body p-4">
                        <p className="text-xs text-base-content/60 uppercase">
                            Multiplicador
                        </p>
                        <p className="font-semibold">
                            {priceTable.baseMultiplier || 1}x
                        </p>
                    </div>
                </div>
            </div>

            {/* Items Section */}
            <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                <div className="card-body">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="card-title text-lg">
                            Itens da Tabela ({items.length})
                        </h2>
                        <div className="flex items-center gap-2">
                            <div className="relative">
                                <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/40" />
                                <input
                                    type="text"
                                    placeholder="Buscar itens..."
                                    value={searchTerm}
                                    onChange={e =>
                                        setSearchTerm(e.target.value)
                                    }
                                    className="input input-bordered input-sm pl-9 w-64"
                                />
                            </div>
                            {canCreate && (
                                <>
                                    <button
                                        onClick={() =>
                                            navigate(
                                                `/billing/price-tables/${id}/itens/importar`
                                            )
                                        }
                                        className="btn btn-ghost btn-sm gap-2"
                                    >
                                        <MdUpload className="text-lg" />
                                        Importar
                                    </button>
                                    <button
                                        onClick={() =>
                                            navigate(
                                                `/billing/price-tables/${id}/itens/add`
                                            )
                                        }
                                        className="btn btn-secondary btn-sm gap-2"
                                    >
                                        <MdAdd className="text-lg" />
                                        Novo Item
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {loadingItems ? (
                        <div className="flex justify-center py-8">
                            <span className="loading loading-spinner loading-md"></span>
                        </div>
                    ) : filteredItems.length === 0 ? (
                        <div className="text-center py-8 text-base-content/60">
                            <MdTableChart className="text-4xl mx-auto mb-2 opacity-40" />
                            <p>Nenhum item cadastrado</p>
                            {canCreate && (
                                <button
                                    onClick={() =>
                                        navigate(
                                            `/billing/price-tables/${id}/itens/add`
                                        )
                                    }
                                    className="btn btn-secondary btn-sm mt-4 gap-2"
                                >
                                    <MdAdd className="text-lg" />
                                    Adicionar Item
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Código</th>
                                        <th>Procedimento</th>
                                        <th>Categoria</th>
                                        <th className="text-right">
                                            Valor Base
                                        </th>
                                        <th className="text-right">
                                            Coeficiente
                                        </th>
                                        <th className="text-right">
                                            Valor Final
                                        </th>
                                        {(canUpdate || canDelete) && <th></th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredItems.map(item => (
                                        <tr key={item.id} className="hover">
                                            <td className="font-mono text-sm">
                                                {item.procedureCode}
                                            </td>
                                            <td>{item.procedureName}</td>
                                            <td>
                                                <span className="badge badge-ghost badge-sm">
                                                    {item.category || '-'}
                                                </span>
                                            </td>
                                            <td className="text-right">
                                                {formatCurrency(item.baseValue)}
                                            </td>
                                            <td className="text-right">
                                                {item.coefficient || 1}x
                                            </td>
                                            <td className="text-right font-semibold">
                                                {formatCurrency(
                                                    item.finalValue
                                                )}
                                            </td>
                                            {(canUpdate || canDelete) && (
                                                <td>
                                                    <div className="flex items-center gap-1 justify-end">
                                                        {canUpdate && (
                                                            <button
                                                                onClick={() =>
                                                                    navigate(
                                                                        `/billing/price-tables/${id}/itens/${item.id}/edit`
                                                                    )
                                                                }
                                                                className="btn btn-ghost btn-xs"
                                                            >
                                                                <MdEdit className="text-lg" />
                                                            </button>
                                                        )}
                                                        {canDelete && (
                                                            <button
                                                                onClick={() =>
                                                                    setDeleteItemId(
                                                                        item.id
                                                                    )
                                                                }
                                                                className="btn btn-ghost btn-xs text-error"
                                                            >
                                                                <MdDelete className="text-lg" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Modal */}
            {showDeleteModal && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg">
                            Confirmar exclusão
                        </h3>
                        <p className="py-4">
                            Tem certeza que deseja excluir esta tabela de
                            preços? Esta ação não pode ser desfeita e todos os
                            itens serão removidos.
                        </p>
                        <div className="modal-action">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                className="btn btn-ghost"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleDelete}
                                className="btn btn-error"
                            >
                                Excluir
                            </button>
                        </div>
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={() => setShowDeleteModal(false)}
                    ></div>
                </div>
            )}

            {/* Duplicate Modal */}
            {showDuplicateModal && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg">Duplicar Tabela</h3>
                        <div className="py-4">
                            <label className="label">
                                <span className="label-text">
                                    Nome da nova tabela
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                value={duplicateName}
                                onChange={e => setDuplicateName(e.target.value)}
                                placeholder="Nome da tabela"
                            />
                        </div>
                        <div className="modal-action">
                            <button
                                onClick={() => {
                                    setShowDuplicateModal(false)
                                    setDuplicateName('')
                                }}
                                className="btn btn-ghost"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleDuplicate}
                                className="btn btn-secondary"
                                disabled={!duplicateName.trim()}
                            >
                                Duplicar
                            </button>
                        </div>
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={() => {
                            setShowDuplicateModal(false)
                            setDuplicateName('')
                        }}
                    ></div>
                </div>
            )}

            {/* Delete Item Modal */}
            {deleteItemId && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg">
                            Confirmar exclusão
                        </h3>
                        <p className="py-4">
                            Tem certeza que deseja remover este item?
                        </p>
                        <div className="modal-action">
                            <button
                                onClick={() => setDeleteItemId(null)}
                                className="btn btn-ghost"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => handleDeleteItem(deleteItemId)}
                                className="btn btn-error"
                            >
                                Remover
                            </button>
                        </div>
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={() => setDeleteItemId(null)}
                    ></div>
                </div>
            )}
        </div>
    )
}
